import type { Landmarks, HandId, Vec2 } from '@store/handStore';
import { FINGER_TIPS, LM } from '@engine/hand/landmarks';
import { crossedStringsOrdered } from '@engine/scale/layout';
import { GESTURE } from '@config/constants';

export type PluckFinger = 'thumb' | 'index' | 'middle';

const PLUCK_FINGERS: readonly PluckFinger[] = ['thumb', 'index', 'middle'];

/**
 * Per-finger "reference joint" — the joint the TIP must cross to fire a
 * pluck. Tighter joint (closer to TIP) = stricter; looser joint (closer
 * to palm) = easier to trigger.
 *
 *   thumb : IP  (thumb has no DIP — IP is its one inter-phalangeal joint)
 *   index : DIP (standard rule)
 *   middle: PIP (looser per user spec — middle needs less travel to fire)
 *
 * Legacy path still reads DIP + PIP velocity — keep both maps around. */
const FINGER_DIP: Record<PluckFinger, number> = {
  thumb: LM.THUMB_IP,
  index: LM.INDEX_DIP,
  middle: LM.MIDDLE_DIP,
};
const FINGER_PIP: Record<PluckFinger, number> = {
  thumb: LM.THUMB_IP,
  index: LM.INDEX_PIP,
  middle: LM.MIDDLE_PIP,
};
const FINGER_REF_JOINT: Record<PluckFinger, number> = {
  thumb: LM.THUMB_IP,
  index: LM.INDEX_PIP,
  middle: LM.MIDDLE_PIP,
};

/** MCP landmark per finger — used for the proximal triplet in gliss
 *  detection (PIP/DIP/MCP all moving together = rigid translation). */
const FINGER_MCP: Record<PluckFinger, number> = {
  thumb: LM.THUMB_MCP,
  index: LM.INDEX_MCP,
  middle: LM.MIDDLE_MCP,
};

/** Per-string refractory — a single string can't retrigger within this
 *  window IN THE SAME SWEEP DIRECTION. Prevents wobble-driven doubles
 *  (tip hovering on a string's Y line). Shortened from 60 to 40 to give
 *  slow sweeps a higher maximum string-per-second rate without gaps. */
const PER_STRING_REFRACTORY_MS = 40;

/** Even shorter refractory when the sweep direction REVERSES on the
 *  same string — allows a sweep-down-then-back-up pattern to fire the
 *  string twice (which is musically correct), while still absorbing
 *  tracking jitter that would otherwise fire on every frame boundary. */
const REVERSAL_REFRACTORY_MS = 25;

/** After a gliss event fires in direction X, the opposite-direction
 *  gliss on the same hand is suppressed for this long. Kills the "ghost
 *  upward gliss at the end of a downward gliss" that happens because the
 *  hand reflexively rebounds, carrying the thumb tip across string lines
 *  on its way up. */
const GLISS_REBOUND_SUPPRESS_MS = 280;

/** How far back to consult the tip-velocity history for the pluck gate.
 *  A soft stroke's peak velocity may come 1-2 frames before the crossing
 *  frame; reading "max |velTip| over this window" catches those instead
 *  of requiring the crossing frame itself to hit the threshold. 250 ms
 *  (up from 100 ms) carries the peak through the decelerating tail of a
 *  long gliss — calibration shows the camera runs at ~22-25 fps and ~25%
 *  of gliss frames have near-zero instantaneous velocity, so a 100 ms
 *  window would drop the peak on slow-ending sweeps. */
const PLUCK_VEL_HISTORY_MS = 250;

/** Low but nonzero MCP-velocity floor for THUMB gliss. Without any floor,
 *  the rebound after a gliss-down fires thumb-gliss-up (thumb tip + IP
 *  both drift upward together → ratio stays low → classified as gliss).
 *  0.07 is below real slow thumb sweeps (calibration p50 ≈ 0.67 for fast,
 *  median slow still > 0.15) but well above rebound drift. */
const THUMB_GLISS_MCP_MIN = 0.07;

/** Pluck-vs-gliss discriminator for index/middle: per-finger ratio of
 *  |velTip| to |velDIP|. Self-contained — no cross-finger comparison,
 *  so a tracking glitch on a non-playing finger can't corrupt the
 *  decision on this one.
 *
 *  Physics: in a GLISS the whole finger translates rigidly with the
 *  hand — tip and DIP move together → ratio ≈ 1. In a PLUCK the finger
 *  pivots at the knuckle — tip swings much more than DIP → ratio ≥ 1.5.
 *
 *  Calibration 2026-04-23 p50 ratios:
 *    gliss_index_*    1.01 - 1.13         (tip and DIP co-moving)
 *    gliss_middle_*   1.02 - 1.10
 *    pluck_index_*    1.43 - 1.65         (tip pivoting at knuckle)
 *    pluck_middle_*   1.54 - 1.71
 *
 *  1.3 sits cleanly in the gap between gliss p95 (≤ ~1.22 for most
 *  buckets) and pluck p50 (≥ 1.43). Under this threshold → translate
 *  (gliss), at or above → flick (pluck). */
const TIP_DIP_TRANSLATE_MAX_RATIO = 1.3;
/** Floor for the denominator so a near-zero DIP velocity (noise/hand
 *  still) doesn't explode the ratio. The `fastEnough` gate already
 *  filters noise frames, but this keeps the math stable. */
const TIP_DIP_VEL_FLOOR = 0.02;

/** Pluck-vs-gliss discriminator for THUMB, using the same per-finger
 *  kinematics idea (tip vs. IP — thumb's only inter-phalangeal joint).
 *    Gliss (thumb rotating at MCP, tip and IP move together) → ratio ≈ 1
 *    Pluck (thumb knuckling at IP, tip swings more than IP)  → ratio ≥ 1.3
 *  Calibration 2026-04-23 p50:
 *    gliss_thumb_*  1.06 − 1.11
 *    pluck_thumb_*  1.30 − 1.50
 *  1.25 sits cleanly in the gap. This is why the thumb gliss wasn't
 *  firing: the old cross-finger check said "thumb moving, others
 *  still → pluck", but a real thumb gliss on a stationary hand has
 *  exactly that signature, so the own-finger ratio is the honest test. */
const THUMB_TIP_IP_TRANSLATE_MAX_RATIO = 1.25;
const THUMB_TIP_IP_VEL_FLOOR = 0.02;

/** Velocity shaping window for PLUCK dynamics — input is tip velocity
 *  (how fast the finger flicks past PIP). Below MIN → shaped 0, above
 *  MAX → shaped 1. Tuned from calibration: slow-pluck tip p50 ≈ 0.25,
 *  fast-pluck peaks 0.7-0.9. */
const VEL_DYNAMIC_MIN = 0.08;
const VEL_DYNAMIC_MAX = 0.9;

/** Gliss dynamics window for index/middle — input is tip-minus-DIP curl
 *  depth. Volume is controlled by how curled the finger is. A fully
 *  straight finger (delta very negative) doesn't qualify as gliss at
 *  all — "hơi cong" (slight curl) is required for the fingertip to
 *  actually meet the strings.
 *    tip-DIP < REQUIRED_MIN   → finger too straight → NO gliss
 *    tip-DIP = SOFT          → minimal curl → soft
 *    tip-DIP = LOUD          → curled/nail-edge brush → loud
 *  Calibration 2026-04-23:
 *    play_stance p50 = -0.073 (fully extended)
 *    gliss p5-p50    = -0.08 to -0.06 (slight-to-moderate curl)
 *    gliss p95       = 0 to +0.04 (curled)
 *  REQUIRED_MIN = -0.06 sits above play_stance so a rigid-extended
 *  finger can't sweep-fire even if the hand translates. */
const GLISS_CURL_REQUIRED_MIN = -0.06;
const GLISS_CURL_SOFT = -0.06;
const GLISS_CURL_LOUD = 0.04;

/** Thumb gliss gate — scale-invariant 2D extension ratio:
 *    dist(thumb_tip, middle_MCP) / dist(wrist, middle_MCP)
 *  The palm length (wrist ↔ middle MCP) is a stable normalizer so the
 *  same threshold works regardless of hand size or camera distance.
 *
 *  Semantics:
 *    thumb curled toward palm (quặp)   → tip near middle MCP  → low ratio → NO gliss
 *    thumb in play stance (relaxed)    → ratio ~0.5–0.7                → NO gliss
 *    thumb actively extended for sweep → ratio ~0.85+                  → gliss fires
 *
 *  Calibration 2026-04-23 percentiles across 3 sessions:
 *    bend_still      p95 ≈ 0.46   (cupped bend posture)
 *    mute_accept     p95 ≈ 0.70   (fist; tail from wrist/thumb motion)
 *    play_stance     p95 ≈ 0.76   (relaxed ready, TIGHT distribution)
 *    thumb_gliss     p50 ≈ 0.86 – 1.14
 *
 *  MIN = 0.80 sits above play_stance p95 so a thumb at rest doesn't
 *  trip the gate on incidental wrist lifts. HYSTERESIS = 0.30 sits
 *  below mid-sweep dips (gliss loud p5 can reach 0.27 as the thumb
 *  naturally curls slightly between phases) but above a genuinely
 *  quặp pose — once a sweep starts, it won't be chopped.
 *
 *  Signal does NOT cleanly separate soft/medium/loud — it's a GATE
 *  only, and velocity shapes dynamics (harder sweep = louder). */
const THUMB_GLISS_EXT_MIN = 0.80;
/** Relaxed gate applied during hysteresis. Mid-sweep the ratio can
 *  dip well below 0.80 as the thumb curls between phases of the
 *  motion; without a much looser hysteresis the sweep chops off 3–4
 *  strings short. 0.30 is below almost all active-sweep frames (p5)
 *  but still rejects a deeply curled / fisted thumb. */
const THUMB_GLISS_EXT_MIN_HYSTERESIS = 0.30;
/** Short EMA on the 2D extension ratio to absorb 1-frame jitter. */
const THUMB_EXT_EMA_TAU_S = 0.04;

/** Continuation hysteresis — within this window after a thumb gliss
 *  event, the gate uses the relaxed distance threshold. Prevents a
 *  brief tracking wobble mid-sweep from cutting the sweep in half. */
const THUMB_GLISS_HYSTERESIS_MS = 180;

/** Exponent applied AFTER the linear normalization. 1.0 = linear (no
 *  shaping) — slow plucks keep their normalized weight instead of being
 *  squared/cubed down into silence. Previous values (2.2, then 1.4) made
 *  slow plucks effectively inaudible because a normalized 0.18 shaped
 *  to 0.06-0.09. With 1.0 the curve is faithful to tip velocity, which
 *  is the measurement the player actually controls. */
const VEL_DYNAMIC_EXPONENT = 1.0;

const clamp01 = (v: number): number => Math.max(0, Math.min(1, v));

/** Map raw tip velocity → 0..1 with an expanded dynamic range. Linear
 *  normalization first, then a power curve so quiet/loud separation is
 *  emphasized at playback. */
const shapeVelocity = (absVel: number): number => {
  const linear = clamp01((absVel - VEL_DYNAMIC_MIN) / (VEL_DYNAMIC_MAX - VEL_DYNAMIC_MIN));
  return Math.pow(linear, VEL_DYNAMIC_EXPONENT);
};

/** Map index/middle tip-minus-DIP curl depth → 0..1 for gliss dynamics.
 *  Returns null when the finger is too straight to be considered a
 *  gliss pose at all — a rigid-extended fingertip pointing up doesn't
 *  brush strings. More curl = louder (nail-edge brush vs flat-pad). */
const shapeGlissVelocity = (tipMinusDip: number): number | null => {
  if (tipMinusDip < GLISS_CURL_REQUIRED_MIN) return null;
  const normalized = (tipMinusDip - GLISS_CURL_SOFT) / (GLISS_CURL_LOUD - GLISS_CURL_SOFT);
  const clamped = clamp01(normalized);
  return 0.15 + 0.85 * clamped;
};

/** Thumb gliss gate + dynamics (2D extension ratio). Returns null when
 *  the thumb is too curled to be in a gliss posture; otherwise returns
 *  loudness [0.15..1.0] shaped from tip speed (faster sweep = louder,
 *  same intuition as a physical instrument). Floor 0.15 matches the
 *  index/middle gliss floor so a soft thumb sweep can be as quiet as
 *  a soft index/middle gliss. The gate tightens for fresh entries and
 *  relaxes during hysteresis. */
const shapeThumbGliss2D = (
  extensionRatio: number,
  absVelTipY: number,
  inHysteresis: boolean,
): number | null => {
  const gate = inHysteresis
    ? THUMB_GLISS_EXT_MIN_HYSTERESIS
    : THUMB_GLISS_EXT_MIN;
  if (extensionRatio < gate) return null;
  const linear = clamp01(
    (absVelTipY - VEL_DYNAMIC_MIN) / (VEL_DYNAMIC_MAX - VEL_DYNAMIC_MIN),
  );
  return 0.15 + 0.85 * linear;
};

export interface PluckEvent {
  hand: HandId;
  finger: PluckFinger;
  stringIndex: number;
  velocity: number; // 0..1
  at: number;
  /** `pluck` = single-finger flick (intentional strike). `gliss` = tip
   *  swept across multiple string lines while the hand translated.
   *  Consumers can gate gliss firings on classifier mode so lifting the
   *  hand to reposition doesn't fire a cascade of phantom sweeps. */
  type: 'pluck' | 'gliss';
}

export interface PluckDetectorSettings {
  /** Y positions (0..1) for every string, in string-index order. Owner
   *  recomputes this when tuning / layout-mode / string-count changes.
   *  Using the array (instead of stringCount) lets the detector honour
   *  cents-based or inverted layouts without any extra plumbing. */
  stringYs: number[];
  minVelocity: number;
  refractoryMs: number;
}

/**
 * Per-finger flick-vs-translate classification using DIP + PIP velocity
 * (MCP is a near-stationary reference during a pluck):
 *
 *   |velDIP| / |velPIP| ≥ FLICK_DIP_PIP_RATIO (2.0)
 *     → finger is pivoting with DIP moving much more than PIP → FLICK
 *   |velPIP| / |velDIP| ≥ FLICK_PIP_DIP_RATIO (1.5)
 *     → DIP is unreliable (off-frame / stuck) and PIP carries motion
 *     → still a FLICK (fallback)
 *   Neither ratio triggers, but max(|velDIP|, |velPIP|) ≥ minVelocity
 *     → whole finger TRANSLATES → GLISSANDO
 *
 * String crossing is read from the faster of DIP and PIP on the given
 * frame, so losing DIP to the frame edge doesn't drop events.
 */
export class PluckDetector {
  private prevTipY: Record<PluckFinger, number> = { thumb: -1, index: -1, middle: -1 };
  private prevRefY: Record<PluckFinger, number> = { thumb: -1, index: -1, middle: -1 };
  private prevDipY: Record<PluckFinger, number> = { thumb: -1, index: -1, middle: -1 };
  private prevPipY: Record<PluckFinger, number> = { thumb: -1, index: -1, middle: -1 };
  private prevMcpY: Record<PluckFinger, number> = { thumb: -1, index: -1, middle: -1 };
  /** Thumb uses TIP and IP positions as (x,y) pairs — it needs both axes
   *  for velocity ratio, unlike index/middle which only track Y. Sentinel
   *  x<0 means "uninitialized"; first frame seeds without firing events. */
  private prevThumbTip: Vec2 = { x: -1, y: -1 };
  private prevThumbIp: Vec2 = { x: -1, y: -1 };
  private prevT = 0;
  /** Most recent flick event per finger on this hand. Per-finger (not
   *  hand-wide) so octave plucking (bốc quản 8 — thumb + index fired
   *  together an octave apart) can actually fire both events. A shared
   *  hand-wide lock would block the second finger for 300ms and kill
   *  the gesture. Same-finger coupled motion is still suppressed because
   *  that finger's own refractory stays in effect. */
  private lastFlickAtByFinger: Record<PluckFinger, number> = {
    thumb: -Infinity,
    index: -Infinity,
    middle: -Infinity,
  };
  /** Per-string last-fire timestamp — used by `PER_STRING_REFRACTORY_MS`
   *  to reject wobble-driven double-fires (tip hovering on a string's Y
   *  line) and give adjacent-string plucks audible breathing room. */
  private lastFireOnString: Record<number, number> = {};
  /** Direction ('down' | 'up') of the last fire on a string — lets us
   *  use a shorter refractory when the sweep reverses direction on the
   *  same string (musically distinct event) vs. jittering in the same
   *  direction (tracking noise). */
  private lastFireDir: Record<number, 'down' | 'up'> = {};
  /** Most-recent velocity samples per finger (abs value, with timestamp) —
   *  consulted by the pluck path so a SOFT stroke that briefly accelerated
   *  a few frames before the crossing still fires. Single-frame peak is too
   *  noisy; a sliding window gives the intuitive "did this finger snap in
   *  the last 100ms?" answer. */
  private velHistory: Record<PluckFinger, Array<{ t: number; v: number }>> = {
    thumb: [],
    index: [],
    middle: [],
  };
  /** Last-gliss timestamps per direction — the motion after a gliss-down
   *  tends to bounce the hand back up, which the thumb-gliss path would
   *  otherwise fire on (ghost upward notes). After any gliss event fires in
   *  one direction, the opposite direction is suppressed for this window. */
  private lastGlissAt: { down: number; up: number } = {
    down: -Infinity,
    up: -Infinity,
  };
  /** EMA-smoothed thumb extension ratio (scale-invariant) — the gate
   *  signal for thumb gliss. Seeded on first read, reset to null on
   *  dropout so the next engagement starts from the freshly observed
   *  pose instead of a stale value. */
  private thumbExtEma: number | null = null;

  constructor(
    public readonly hand: HandId,
    private settings: PluckDetectorSettings,
  ) {}

  updateSettings(s: Partial<PluckDetectorSettings>): void {
    this.settings = { ...this.settings, ...s };
  }

  /** Append a velocity sample for `finger` and prune anything older than
   *  PLUCK_VEL_HISTORY_MS. Returns the PEAK absolute velocity in the
   *  retained window, which the pluck gate consults instead of the
   *  single-frame value. Soft plucks often have a 1-2 frame acceleration
   *  slightly before the crossing frame — this recovers them. */
  private recordAndPeakVel(finger: PluckFinger, absVel: number, now: number): number {
    const buf = this.velHistory[finger];
    buf.push({ t: now, v: absVel });
    const cutoff = now - PLUCK_VEL_HISTORY_MS;
    while (buf.length && buf[0]!.t < cutoff) buf.shift();
    let peak = 0;
    for (const s of buf) if (s.v > peak) peak = s.v;
    return peak;
  }

  /** Returns 0..N pluck events fired this frame. */
  step(lm: Landmarks | null, now: number): PluckEvent[] {
    if (!lm) {
      this.reset();
      return [];
    }
    const dtRaw = now - this.prevT;
    // Stale-frame re-seed. Lowered from 150 ms → 80 ms: at ~22-25 fps
    // the normal frame interval is 40-47 ms, so anything above 80 ms
    // is a tracking dropout (hand briefly off-screen, low-confidence
    // frame, etc.). Computing velocity across the dropout would report
    // every string between the old and new tip position as crossed in
    // one frame, firing a burst of gliss events ("tùm lum"). Re-seeding
    // silently is the honest choice — we can't know what happened while
    // the hand was invisible.
    if (this.prevT > 0 && dtRaw > 80) {
      for (const finger of PLUCK_FINGERS) {
        this.prevTipY[finger] = lm[FINGER_TIPS[finger]]!.y;
        this.prevRefY[finger] = lm[FINGER_REF_JOINT[finger]]!.y;
        this.prevDipY[finger] = lm[FINGER_DIP[finger]]!.y;
        this.prevPipY[finger] = lm[FINGER_PIP[finger]]!.y;
        this.prevMcpY[finger] = lm[FINGER_MCP[finger]]!.y;
      }
      const tTip = lm[LM.THUMB_TIP]!;
      const tIp = lm[LM.THUMB_IP]!;
      this.prevThumbTip = { x: tTip.x, y: tTip.y };
      this.prevThumbIp = { x: tIp.x, y: tIp.y };
      this.thumbExtEma = null;
      this.prevT = now;
      return [];
    }

    const events: PluckEvent[] = [];
    const dt = Math.max(1, dtRaw) / 1000;

    // Hand-level gate for glissando: if ALL 3 pluck fingers are deeply
    // bent (TIP at or below its own PIP / IP for thumb), the hand is in
    // a full cup / bend posture — skip gliss entirely. Per-finger "not
    // bent" gate below still applies to each gliss candidate.
    const idxBent = lm[LM.INDEX_TIP]!.y >= lm[LM.INDEX_PIP]!.y;
    const midBent = lm[LM.MIDDLE_TIP]!.y >= lm[LM.MIDDLE_PIP]!.y;
    const thumbBent = lm[LM.THUMB_TIP]!.y >= lm[LM.THUMB_IP]!.y;
    const allThreeBent = idxBent && midBent && thumbBent;

    // "Playing finger" for gliss attribution. Calibration 2026-04-23:
    // during gliss, ALL fingers look straight most of the time (p50
    // tip_over_pip ≈ 2.2), but the finger actually brushing a string
    // momentarily curls more (p5 drops to 0.7–1.7 on the playing finger,
    // stays ≥ 2.0 on the idle ones). So: the more-curled of index/middle
    // at THIS frame is the one we attribute the gliss event to, letting
    // downstream code tell "index gliss" from "middle gliss" from "double
    // gliss" by inspecting which label wins each frame. */
    const idxCurlDepth =
      lm[LM.INDEX_TIP]!.y - lm[LM.INDEX_PIP]!.y; // positive = tip below PIP
    const midCurlDepth = lm[LM.MIDDLE_TIP]!.y - lm[LM.MIDDLE_PIP]!.y;
    const playingFinger: 'index' | 'middle' =
      idxCurlDepth > midCurlDepth ? 'index' : 'middle';

    // ---- Index + middle.
    //   PLUCK : TIP crosses PIP AND MCP almost stationary (finger flick
    //           with palm still). One string, hand-wide flick refractory.
    //   GLISS : MCP moving (hand translating) + TIP moves down, no PIP
    //           crossing this frame. Fires every string swept.
    //   BEND-posture gate: if all 3 pluck fingers are deeply bent, the
    //     hand is cupped → skip gliss (classifier handles bend).
    for (const finger of ['index', 'middle'] as const) {
      const tipY = lm[FINGER_TIPS[finger]]!.y;
      const refY = lm[FINGER_REF_JOINT[finger]]!.y;
      const dipY = lm[FINGER_DIP[finger]]!.y;
      const pipY = lm[FINGER_PIP[finger]]!.y;
      const mcpY = lm[FINGER_MCP[finger]]!.y;
      const prevTipY = this.prevTipY[finger];
      const prevRefY = this.prevRefY[finger];
      const prevMcpY = this.prevMcpY[finger];
      if (prevTipY < 0 || prevRefY < 0 || prevMcpY < 0) {
        this.prevTipY[finger] = tipY;
        this.prevRefY[finger] = refY;
        this.prevDipY[finger] = dipY;
        this.prevPipY[finger] = pipY;
        this.prevMcpY[finger] = mcpY;
        continue;
      }

      const velTipY = (tipY - prevTipY) / dt;
      const absVelTip = Math.abs(velTipY);
      const velDipY = (dipY - this.prevDipY[finger]) / dt;
      const absVelDip = Math.abs(velDipY);
      const movingDown = velTipY > 0;
      // Peak |velTip| over the last ~100ms. Lets soft plucks fire even when
      // the very frame of the PIP crossing is slower than `minVelocity`.
      const peakVelTip = this.recordAndPeakVel(finger, absVelTip, now);
      const fastEnough = peakVelTip >= this.settings.minVelocity;

      const wasAbove = prevTipY < prevRefY;
      const nowAtOrBelow = tipY >= refY;
      const justCrossed = wasAbove && nowAtOrBelow;

      // Pluck-vs-gliss by per-finger tip/DIP velocity ratio. See
      // TIP_DIP_TRANSLATE_MAX_RATIO comment for calibration: gliss
      // p50 ≈ 1.05 (finger rigid with hand), pluck p50 ≈ 1.55 (finger
      // pivoting at knuckle). Self-contained per finger — a tracking
      // glitch on middle can't corrupt the decision on index.
      const tipOverDip = absVelTip / Math.max(absVelDip, TIP_DIP_VEL_FLOOR);
      const isTranslate = tipOverDip < TIP_DIP_TRANSLATE_MAX_RATIO;
      const isFlick = !isTranslate;

      if (movingDown && fastEnough) {
        const crossed = crossedStringsOrdered(
          prevTipY,
          tipY,
          'down',
          this.settings.stringYs,
        );
        if (crossed.length > 0) {
          // Decision is driven by MOTION SIGNATURE, not crossing count:
          //   PLUCK = clear finger flick (tip crosses PIP this frame +
          //     others still). A hard flick can zip past 2-3 strings
          //     in a single frame; collapse to ONE event on crossed[0]
          //     ("pluck allow 1 first string sounds per stroke").
          //   GLISS = any crossing without the flick signature —
          //     including mid-sweep frames where the tip is already
          //     below PIP. A transient ratio glitch inside a real
          //     sweep has justCrossed=false, so it falls safely to
          //     the gliss branch and can't punch a hole in the sweep.
          if (isFlick && justCrossed) {
            // PLUCK — finger flick. One string, hand-wide refractory.
            const stringIndex = crossed[0]!;
            const last = this.lastFireOnString[stringIndex] ?? -Infinity;
            const lastDir = this.lastFireDir[stringIndex];
            const gate =
              lastDir && lastDir !== 'down'
                ? REVERSAL_REFRACTORY_MS
                : PER_STRING_REFRACTORY_MS;
            if (
              now - this.lastFlickAtByFinger[finger] >= GESTURE.FLICK_REFRACTORY_MS &&
              now - last >= gate
            ) {
              this.lastFlickAtByFinger[finger] = now;
              this.lastFireOnString[stringIndex] = now;
              this.lastFireDir[stringIndex] = 'down';
              events.push({
                hand: this.hand,
                finger,
                stringIndex,
                velocity: shapeVelocity(absVelTip),
                at: now,
                type: 'pluck',
              });
            }
          } else if (
            !allThreeBent &&
            now - this.lastGlissAt.up >= GLISS_REBOUND_SUPPRESS_MS
          ) {
            // GLISS — hand translating. Volume comes from the playing
            // finger's curl depth; if that finger is too straight
            // (rigid extended, play_stance), shapeGlissVelocity returns
            // null and no events fire ("hơi cong" required).
            const playingTipY =
              playingFinger === 'index' ? lm[LM.INDEX_TIP]!.y : lm[LM.MIDDLE_TIP]!.y;
            const playingDipY =
              playingFinger === 'index' ? lm[LM.INDEX_DIP]!.y : lm[LM.MIDDLE_DIP]!.y;
            const glissVel = shapeGlissVelocity(playingTipY - playingDipY);
            if (glissVel !== null) {
              for (const stringIndex of crossed) {
                const last = this.lastFireOnString[stringIndex] ?? -Infinity;
                const lastDir = this.lastFireDir[stringIndex];
                const gate =
                  lastDir && lastDir !== 'down'
                    ? REVERSAL_REFRACTORY_MS
                    : PER_STRING_REFRACTORY_MS;
                if (now - last < gate) continue;
                this.lastFireOnString[stringIndex] = now;
                this.lastFireDir[stringIndex] = 'down';
                this.lastGlissAt.down = now;
                events.push({
                  hand: this.hand,
                  finger: playingFinger,
                  stringIndex,
                  velocity: glissVel,
                  at: now,
                  type: 'gliss',
                });
              }
            }
          }
        }
      }

      this.prevTipY[finger] = tipY;
      this.prevRefY[finger] = refY;
      this.prevDipY[finger] = dipY;
      this.prevPipY[finger] = pipY;
      this.prevMcpY[finger] = mcpY;
    }

    // ---- Thumb.
    //   TIP is always above IP on an extended thumb, so the "TIP crosses
    //   its own IP" rule can't work. Primary signal is the TIP/IP
    //   velocity ratio (classic flick signature):
    //     |velTipY| / |velIpY| ≥ 2.0  AND MCP stationary  → PLUCK (1 string)
    //     ratio below 2.0 (tip + ip moving together)      → GLISS (all strings)
    //   MCP is a belt-and-suspenders gate for pluck (thumb MCP drifts
    //   off-frame often — we use it only to confirm a flick, not to
    //   reject gliss).
    {
      const tipNow = lm[LM.THUMB_TIP]!;
      const ipNow = lm[LM.THUMB_IP]!;
      const mcpNow = lm[LM.THUMB_MCP]!;
      if (this.prevThumbTip.x < 0 || this.prevThumbIp.x < 0) {
        this.prevThumbTip = { x: tipNow.x, y: tipNow.y };
        this.prevThumbIp = { x: ipNow.x, y: ipNow.y };
        this.prevMcpY.thumb = mcpNow.y;
      } else {
        const velTipY = (tipNow.y - this.prevThumbTip.y) / dt; // negative = up
        const velIpY = (ipNow.y - this.prevThumbIp.y) / dt;
        const absVelTipY = Math.abs(velTipY);
        const absVelIpY = Math.abs(velIpY);
        const velMcpY = this.prevMcpY.thumb < 0 ? 0 : (mcpNow.y - this.prevMcpY.thumb) / dt;
        const absVelMcpY = Math.abs(velMcpY);

        const movingUp = velTipY < 0;
        // Peak |velTip| for pluck gate (soft-pluck recovery, same idea as
        // index/middle path).
        const peakVelTipY = this.recordAndPeakVel('thumb', absVelTipY, now);
        const fastEnough = peakVelTipY >= this.settings.minVelocity;
        // Thumb flick test: own-finger tip/IP ratio (same kinematics
        // principle as index/middle's tip/DIP test). Cross-finger
        // ratio was wrong for thumb: a real thumb gliss on a still
        // hand has thumb moving alone → cross-finger ratio high → it
        // misfired as pluck. Own-finger ratio captures the right
        // signal: tip and IP co-moving (thumb rotating at MCP =
        // translate) vs tip-pivots-on-IP (pluck).
        const tipOverIp = absVelTipY / Math.max(absVelIpY, THUMB_TIP_IP_VEL_FLOOR);
        const isFlick = tipOverIp >= THUMB_TIP_IP_TRANSLATE_MAX_RATIO;

        // Scale-invariant thumb extension ratio:
        //   dist(thumb_tip, middle_MCP) / dist(wrist, middle_MCP)
        // Normalising by palm length makes the same threshold work
        // across hand sizes and camera distances. Smoothed with a
        // short EMA to absorb single-frame jitter.
        const wristLm = lm[LM.WRIST]!;
        const middleMcp = lm[LM.MIDDLE_MCP]!;
        const palm = Math.hypot(
          wristLm.x - middleMcp.x,
          wristLm.y - middleMcp.y,
        );
        const rawExt =
          palm > 1e-4
            ? Math.hypot(tipNow.x - middleMcp.x, tipNow.y - middleMcp.y) / palm
            : 0;
        const alpha = 1 - Math.exp(-dt / THUMB_EXT_EMA_TAU_S);
        this.thumbExtEma =
          this.thumbExtEma === null
            ? rawExt
            : this.thumbExtEma + alpha * (rawExt - this.thumbExtEma);

        // Continuation latch: within the hysteresis window after the last
        // thumb gliss event, treat the current frame as "sweep in progress"
        // and relax the gates that can frame-flip mid-sweep:
        //   · isFlick (tipOverIp ratio blips on thumb IP jitter)
        //   · allThreeBent (other fingers briefly curl)
        //   · fastEnough (tip decelerates at the end of a sweep)
        //   · movingUp (tip stalls / blips at the apex of the sweep —
        //     crossedStringsOrdered still filters by direction, so a
        //     reversed motion produces 0 crossings and fires nothing)
        //   · extension ratio (relaxed by THUMB_GLISS_EXT_MIN_HYSTERESIS
        //     inside the shaper)
        const thumbGlissContinuing =
          now - this.lastGlissAt.up < THUMB_GLISS_HYSTERESIS_MS;

        if ((movingUp || thumbGlissContinuing) && (fastEnough || thumbGlissContinuing)) {
          const crossed = crossedStringsOrdered(
            this.prevThumbTip.y,
            tipNow.y,
            'up',
            this.settings.stringYs,
          );
          if (crossed.length > 0) {
            if (isFlick && !thumbGlissContinuing) {
              // PLUCK — one string, refractory. No longer requires
              // mcpStationary: the other-fingers-still signal already
              // rules out gliss (which would move all tips, including
              // middle/index), so a drifting thumb MCP during a legit
              // pluck shouldn't be penalized.
              const stringIndex = crossed[0]!;
              const last = this.lastFireOnString[stringIndex] ?? -Infinity;
              const lastDir = this.lastFireDir[stringIndex];
              const gate =
                lastDir && lastDir !== 'up'
                  ? REVERSAL_REFRACTORY_MS
                  : PER_STRING_REFRACTORY_MS;
              if (
                now - this.lastFlickAtByFinger.thumb >= GESTURE.FLICK_REFRACTORY_MS &&
                now - last >= gate
              ) {
                this.lastFlickAtByFinger.thumb = now;
                this.lastFireOnString[stringIndex] = now;
                this.lastFireDir[stringIndex] = 'up';
                events.push({
                  hand: this.hand,
                  finger: 'thumb',
                  stringIndex,
                  velocity: shapeVelocity(absVelTipY),
                  at: now,
                  type: 'pluck',
                });
              }
            } else if (
              (!isFlick || thumbGlissContinuing) &&
              (!allThreeBent || thumbGlissContinuing) &&
              now - this.lastGlissAt.down >= GLISS_REBOUND_SUPPRESS_MS
            ) {
              // GLISS — thumb sweep upward. Scale-invariant 2D gate:
              // thumb extension ratio (tip↔middle_MCP / palm length)
              // must clear THUMB_GLISS_EXT_MIN for fresh entries; it
              // relaxes during hysteresis so mid-sweep dips don't
              // chop the sweep. Velocity shapes dynamics (faster =
              // louder). The old Z-based signal was dropped because
              // MediaPipe depth is too noisy to gate cleanly.
              const thumbGlissVel = shapeThumbGliss2D(
                this.thumbExtEma ?? rawExt,
                absVelTipY,
                thumbGlissContinuing,
              );
              if (thumbGlissVel !== null) {
                for (const stringIndex of crossed) {
                  const last = this.lastFireOnString[stringIndex] ?? -Infinity;
                  const lastDir = this.lastFireDir[stringIndex];
                  const gate =
                    lastDir && lastDir !== 'up'
                      ? REVERSAL_REFRACTORY_MS
                      : PER_STRING_REFRACTORY_MS;
                  if (now - last < gate) continue;
                  this.lastFireOnString[stringIndex] = now;
                  this.lastFireDir[stringIndex] = 'up';
                  this.lastGlissAt.up = now;
                  events.push({
                    hand: this.hand,
                    finger: 'thumb',
                    stringIndex,
                    velocity: thumbGlissVel,
                    at: now,
                    type: 'gliss',
                  });
                }
              }
            }
          }
        }

        this.prevThumbTip = { x: tipNow.x, y: tipNow.y };
        this.prevThumbIp = { x: ipNow.x, y: ipNow.y };
        this.prevMcpY.thumb = mcpNow.y;
      }
      // Keep the per-finger Y buffers in sync for the legacy path.
      this.prevTipY.thumb = tipNow.y;
      this.prevRefY.thumb = ipNow.y;
      this.prevDipY.thumb = ipNow.y;
      this.prevPipY.thumb = ipNow.y;
    }

    this.prevT = now;
    return events;
  }

  reset(): void {
    this.prevTipY = { thumb: -1, index: -1, middle: -1 };
    this.prevRefY = { thumb: -1, index: -1, middle: -1 };
    this.prevDipY = { thumb: -1, index: -1, middle: -1 };
    this.prevPipY = { thumb: -1, index: -1, middle: -1 };
    this.prevMcpY = { thumb: -1, index: -1, middle: -1 };
    this.prevThumbTip = { x: -1, y: -1 };
    this.prevThumbIp = { x: -1, y: -1 };
    this.lastFlickAtByFinger = { thumb: -Infinity, index: -Infinity, middle: -Infinity };
    this.lastFireOnString = {};
    this.lastFireDir = {};
    this.velHistory = { thumb: [], index: [], middle: [] };
    this.lastGlissAt = { down: -Infinity, up: -Infinity };
    this.thumbExtEma = null;
  }
}
